import { useEffect, useState } from "react";
import {
  Banknote,
  Smartphone,
  CreditCard,
  Sparkles,
  TrendingUp,
  RefreshCw,
  Crown,
  AlertCircle,
  Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { apiTotals } from "../lib/api";
import { formatINR } from "../lib/menu";

const formatDate = (d) => {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ICONS = {
  Cash: Banknote,
  GPay: Smartphone,
  "Pending Payment": CreditCard,
  Custom: Sparkles,
  Owner: Crown,
};

export default function TodayTotals({ refreshKey }) {
  const [date, setDate] = useState(formatDate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await apiTotals(d);
      setData(res);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(date);
  }, [date, refreshKey]);

  const breakdown = data?.breakdown || { Cash: 0, GPay: 0, "Pending Payment": 0, Custom: 0 };
  const itemEntries = data ? Object.entries(data.item_counts || {}) : [];
  const totalItemsSold = itemEntries.reduce((sum, [, c]) => sum + Number(c || 0), 0);

  return (
    <div data-testid="today-totals-section" className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#1A1918] font-heading">
            Daily Totals
          </h2>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854] mt-1">
            Sales breakdown by payment method
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5854]">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="totals-date-input"
              className="bg-white border border-[#E6E2D8] rounded-md px-3 py-2 text-base focus:border-[#C84C31] focus:ring-2 focus:ring-[#C84C31]/20 outline-none"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => load(date)}
            data-testid="totals-refresh-button"
            className="border-[#E6E2D8] text-[#1A1918] hover:bg-[#F5F2EB] h-10"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Grand total card */}
        <div className="lg:col-span-1 md:col-span-2 bg-[#1A1918] text-white rounded-lg p-6 flex flex-col justify-between min-h-[140px]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
              Total Revenue
            </span>
            <TrendingUp size={16} className="text-[#C84C31]" />
          </div>
          <div>
            <div
              data-testid="total-revenue-value"
              className="text-4xl font-black font-heading tabular-nums"
            >
              {formatINR(data?.total || 0)}
            </div>
            <p className="text-xs text-white/60 mt-1" data-testid="orders-items-summary">
              {data?.order_count || 0} orders · {totalItemsSold} items
            </p>
          </div>
        </div>

        {["Cash", "GPay", "Pending Payment", "Custom"].map((key) => {
          const Icon = ICONS[key] || Sparkles;
          const value = key === "Pending Payment"
            ? (data?.pending_payment_value || 0)
            : (breakdown[key] || 0);
          return (
            <div
              key={key}
              className={`border rounded-lg p-6 flex flex-col justify-between min-h-[140px] ${
                key === "Pending Payment"
                  ? "bg-[#FFF8F0] border-[#F5A623]/40"
                  : "bg-[#FFFCF5] border-[#E6E2D8]"
              }`}
              data-testid={`breakdown-${key}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold uppercase tracking-[0.2em] ${
                  key === "Pending Payment" ? "text-[#F5A623]" : "text-[#5C5854]"
                }`}>
                  {key}
                </span>
                <Icon size={16} className={key === "Pending Payment" ? "text-[#F5A623]" : "text-[#C84C31]"} />
              </div>
              <div>
                <div className={`text-3xl font-black font-heading tabular-nums ${
                  key === "Pending Payment" ? "text-[#F5A623]" : "text-[#1A1918]"
                }`}>
                  {formatINR(value)}
                </div>
                {key === "Pending Payment" && (
                  <p className="text-xs text-[#F5A623] mt-1">not in revenue</p>
                )}
              </div>
            </div>
          );
        })}

        {/* Outstanding card */}
        <div
          className={`rounded-lg p-6 flex flex-col justify-between min-h-[140px] border ${
            (data?.outstanding || 0) > 0
              ? "bg-[#F44336]/8 border-[#F44336]/40"
              : "bg-[#FFFCF5] border-[#E6E2D8]"
          }`}
          data-testid="breakdown-Outstanding"
        >
          <div className="flex items-center justify-between">
            <span className={`text-xs font-bold uppercase tracking-[0.2em] ${
              (data?.outstanding || 0) > 0 ? "text-[#F44336]" : "text-[#5C5854]"
            }`}>
              Outstanding
            </span>
            <AlertCircle
              size={16}
              className={(data?.outstanding || 0) > 0 ? "text-[#F44336]" : "text-[#5C5854]"}
            />
          </div>
          <div>
            <div
              className={`text-3xl font-black font-heading tabular-nums ${
                (data?.outstanding || 0) > 0 ? "text-[#F44336]" : "text-[#1A1918]"
              }`}
              data-testid="outstanding-value"
            >
              {formatINR(data?.outstanding || 0)}
            </div>
            <p className="text-xs text-[#5C5854] mt-1">unpaid amount, not in revenue</p>
          </div>
        </div>

        {/* Owner card */}
        <div
          className="bg-[#FFFCF5] border border-[#E6E2D8] rounded-lg p-6 flex flex-col justify-between min-h-[140px]"
          data-testid="breakdown-Owner"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854]">
              Owner
            </span>
            <Crown size={16} className="text-[#C84C31]" />
          </div>
          <div>
            <div
              className="text-3xl font-black font-heading tabular-nums text-[#1A1918]"
              data-testid="owner-value"
            >
              {formatINR(data?.owner_value || 0)}
            </div>
            <p className="text-xs text-[#5C5854] mt-1">comped, not in revenue</p>
          </div>
        </div>
      </div>

      {data && Object.keys(data.custom_breakdown || {}).length > 0 && (
        <div className="bg-[#FFFCF5] border border-[#E6E2D8] rounded-lg p-6">
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5854] mb-4">
            Custom payment labels
          </h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.custom_breakdown).map(([label, amt]) => (
              <div
                key={label}
                className="bg-[#F5F2EB] border border-[#E6E2D8] rounded-md px-4 py-2 flex items-center gap-3"
              >
                <span className="text-sm font-semibold text-[#1A1918]">{label}</span>
                <span className="text-sm font-bold tabular-nums text-[#C84C31]">
                  {formatINR(amt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {itemEntries.length > 0 && (
        <div className="bg-[#FFFCF5] border border-[#E6E2D8] rounded-lg p-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5854]">
              Items sold
            </h3>
            <span
              data-testid="items-sold-total"
              className="text-xs font-bold uppercase tracking-[0.15em] text-[#1A1918] bg-[#F5F2EB] border border-[#E6E2D8] rounded-md px-3 py-1 tabular-nums"
            >
              Total: {totalItemsSold}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {itemEntries
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => (
                <div
                  key={name}
                  className="flex justify-between border-b border-dashed border-[#E6E2D8] py-2"
                >
                  <span className="text-sm text-[#1A1918] truncate pr-2">{name}</span>
                  <span className="text-sm font-bold tabular-nums text-[#1A1918]">
                    × {count}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
